// Measure the actual rendering runs: the app overrides Fabric's baseline and
// line spacing, so reconstructing those positions from fontSize is unreliable.
function compactLabelInkBounds(object, centerDigitsOnly) {
  const context = object.getMeasuringContext();
  const renderCharDescriptor = Object.getOwnPropertyDescriptor(object, '_renderChar');
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  context.save();
  try {
    context.textBaseline = 'alphabetic';
    context.textAlign = 'left';
    context.direction = 'ltr';
    object._renderChar = function(method, ctx, lineIndex, charIndex, text, x, y) {
      if (lineIndex !== 0 || !text.trim()) return;
      // Center the full label horizontally. Punctuation must not influence
      // vertical centering, even when Fabric emits it as a separate styled run.
      const style = this.getCompleteStyleDeclaration(lineIndex, charIndex);
      ctx.font = this._getFontDeclaration(style);
      const metrics = ctx.measureText(text);
      if (![metrics.actualBoundingBoxLeft, metrics.actualBoundingBoxRight].every(Number.isFinite)) return;
      left = Math.min(left, x - metrics.actualBoundingBoxLeft);
      right = Math.max(right, x + metrics.actualBoundingBoxRight);
      const verticalText = centerDigitsOnly ? text.replace(/[.)]$/, '') : text;
      if (!verticalText) return;
      const verticalMetrics = ctx.measureText(verticalText);
      if (![verticalMetrics.actualBoundingBoxAscent, verticalMetrics.actualBoundingBoxDescent].every(Number.isFinite)) return;
      const baseline = y + Number(style.deltaY || 0);
      top = Math.min(top, baseline - verticalMetrics.actualBoundingBoxAscent);
      bottom = Math.max(bottom, baseline + verticalMetrics.actualBoundingBoxDescent);
    };
    // Reuse the real line/run layout, but intercept drawing so nothing is painted
    // and rendering side effects (baseline recording, shadows) are not triggered.
    object._renderTextCommon(context, 'fillText');
  } finally {
    if (renderCharDescriptor) {
      Object.defineProperty(object, '_renderChar', renderCharDescriptor);
    } else {
      delete object._renderChar;
    }
    context.restore();
  }
  if (![left, top, right, bottom].every(Number.isFinite)) return null;
  return {
    x: (left + right) / 2 - object._getLeftOffset(),
    y: (top + bottom) / 2 - object._getTopOffset(),
  };
}

// Return a one-time translation from the textbox origin to the source ink box.
export function isolatedGlyphTranslation(object, box) {
  if (!box || box.centerInContainer !== true
    || ![box.width, box.height].every(Number.isFinite)) return null;
  const ink = compactLabelInkBounds(object, box.centerDigitsOnly === true);
  if (!ink) return null;
  const dx = (box.width / 2 - ink.x) * (object.scaleX || 1);
  const dy = (box.height / 2 - ink.y) * (object.scaleY || 1);
  const angle = (object.angle || 0) * Math.PI / 180;
  return {x: dx * Math.cos(angle) - dy * Math.sin(angle),
    y: dx * Math.sin(angle) + dy * Math.cos(angle)};
}

// Detected coordinates describe visible ink, whereas Fabric positions a line
// box. Align the first visible row once without changing its line spacing.
export function detectedTextTranslation(object, box) {
  if (!box || object.isCurved || object.path) return null;
  if (box.centerInContainer === true) {
    return isolatedGlyphTranslation(object, box);
  }
  const firstLine = object._textLines?.[0];
  if (!firstLine?.length) return null;
  const context = object.getMeasuringContext();
  let inkTop = Infinity;
  const baseline = object.getHeightOfLine(0) / object.lineHeight * (1 - object._fontSizeFraction);
  context.save();
  try {
    context.textBaseline = 'alphabetic';
    context.textAlign = 'left';
    firstLine.forEach((character, index) => {
      if (!character.trim()) return;
      const style = object.getCompleteStyleDeclaration(0, index);
      context.font = object._getFontDeclaration(style);
      const ascent = context.measureText(character).actualBoundingBoxAscent;
      if (Number.isFinite(ascent)) {
        inkTop = Math.min(inkTop, baseline - ascent + Number(style.deltaY || 0));
      }
    });
  } finally {
    context.restore();
  }
  if (!Number.isFinite(inkTop)) return null;
  const dy = -inkTop * (object.scaleY || 1);
  const angle = (object.angle || 0) * Math.PI / 180;
  return {x: -dy * Math.sin(angle), y: dy * Math.cos(angle)};
}
